import { z } from 'zod';
import { OAuthProviderSchema } from './auth.js';
import { PaginationQuerySchema, createPaginatedResponseSchema } from './common.js';
// =============================================================================
// Calendar Connection
// =============================================================================
/**
 * Returned when initiating the OAuth flow to connect a calendar.
 * The frontend should redirect the user to `authorizeUrl`.
 */
export const CalendarConnectionAuthorizeResponseSchema = z.object({
    authorizationUrl: z.url(),
});
/**
 * POST body for completing the calendar connection OAuth callback.
 * - `code`: the OAuth authorisation code returned by the provider.
 * - `state`: the nonce returned in the provider redirect (must match the persisted OAuthState row).
 * - `lookbackDays`: optional - days of historical events to fetch on initial connect (defaults to 7).
 *   Maps to `CalendarConnection.lookbackDays`. Use `0` to skip historical sync entirely.
 *   Maximum 365. Must be a non-negative integer.
 *   Note: `horizonDays` is not user-configurable at this stage; all connections use the schema
 *   default of 30 days. Adding it is purely additive (new optional field) when required.
 */
export const CalendarConnectionCallbackBodySchema = z.object({
    code: z.string(),
    state: z.string(),
    lookbackDays: z.int().min(0).max(365).optional(),
});
/**
 * A single calendar connection item as returned in list responses.
 * Tokens are never serialised.
 */
export const CalendarConnectionItemSchema = z.object({
    id: z.string(),
    emailAddress: z.string(),
    provider: OAuthProviderSchema,
    status: z.enum(['active', 'disconnected', 'needs_reauth']),
    connectedAt: z.string(),
});
/**
 * Full calendar connection detail (includes `connectedBy`).
 * `connectedBy.email` is intentionally excluded - it is PII and not needed
 * by the UI for calendar connection flows.
 */
export const CalendarConnectionDetailSchema = z.object({
    id: z.string(),
    emailAddress: z.string(),
    provider: OAuthProviderSchema,
    status: z.enum(['active', 'disconnected', 'needs_reauth']),
    connectedAt: z.string(),
    connectedBy: z.object({
        id: z.string(),
        name: z.string(),
    }),
});
export const CalendarConnectionListResponseSchema = createPaginatedResponseSchema(CalendarConnectionItemSchema);
export const DisconnectCalendarConnectionResponseSchema = z.object({
    success: z.boolean(),
});
// =============================================================================
// Stored Calendar Events
// =============================================================================
export const CalendarEventAttendeeSchema = z.object({
    email: z.string(),
    displayName: z.string().optional(),
    responseStatus: z.enum(['accepted', 'tentative', 'declined', 'needsAction']),
    isOrganiser: z.boolean(),
    isSelf: z.boolean(),
});
export const CalendarEventOrganiserSchema = z.object({
    email: z.string(),
    displayName: z.string().optional(),
});
/**
 * Why a calendar match has no draft activity, derived on read from the event
 * and match state. Precedence: cancelled > all-day > declined > no matter.
 * `NOT_CONFIRMED` is an allocation made (by a person or automatically) before
 * calendar matches became proposals: it keeps a draft it already had but never gains one, so
 * "Log time" is the way to add one.
 */
export const CalendarNoActivityReasonSchema = z.enum([
    'EVENT_CANCELLED',
    'ALL_DAY_EVENT',
    'OWNER_DECLINED',
    'NO_PROJECT_MATCH',
    'NOT_CONFIRMED',
]);
/**
 * How the matcher arrived at a (client, project) tuple. Single source of truth
 * for both the email matcher and the calendar matcher; persisted on
 * `calendar_client_matches.projectMatchMethod` / `email_client_matches.matchType`.
 *
 * - `reference_match`: an internal/custom project reference appeared in the
 *   content.
 * - `name_match`: the project name appeared verbatim in the content.
 * - `fuzzy_match`: fuzzy name match against the project name.
 * - `single_project`: content matched the client but no project signal was
 *   found, and the client has exactly one active project (auto-attached).
 */
export const ProjectMatchMethodSchema = z.enum([
    'reference_match',
    'name_match',
    'fuzzy_match',
    'single_project',
]);
/**
 * The chosen client/project match for a stored calendar event. One match per
 * event maximum; null when the event has not been matched to a client yet.
 */
export const CalendarClientMatchSchema = z.object({
    calendarClientMatchId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    clientProjectId: z.string().nullable(),
    clientProjectName: z.string().nullable(),
    projectMatchMethod: ProjectMatchMethodSchema.nullable(),
    projectConfidence: z.number().nullable(),
    clientArchived: z.boolean(),
    clientProjectArchived: z.boolean().nullable(),
    /**
     * The id of the live (non-archived) activity for this match, or null. When
     * the activity has been archived (rejected by the user), activityId is null
     * and activityArchived is true. When no activity exists at all, both are
     * null/false. When a live activity exists, `noActivityReason` is always null.
     */
    activityId: z.uuid().nullable(),
    /**
     * Lifecycle status of the activity sourced from this match, or null when
     * no activity row exists at all. Mirrors email's projection so the row
     * pill can render Draft / Approved / Invoiced / Rejected.
     */
    activityStatus: z.enum(['DRAFT', 'APPROVED', 'INVOICED']).nullable(),
    /**
     * True when the activity sourced from this match has been archived (user
     * rejected the draft). Combined with activityStatus to distinguish "no
     * activity ever" from "activity rejected".
     */
    activityArchived: z.boolean(),
    /**
     * Why no activity was auto-created, or null when one exists. See
     * `CalendarNoActivityReason` for the values and their precedence.
     */
    noActivityReason: CalendarNoActivityReasonSchema.nullable(),
    /**
     * Policy decision recorded at match-write time. True when the reconciler
     * will (or did) create a draft activity: an auto-policy match that passed,
     * a manual match to a matter while the account's auto-drafting is on, or a
     * match the user explicitly promoted (promotion bypasses the auto-draft
     * gate). With auto-drafting off, a manual match stays false. Default false
     * for legacy rows.
     */
    shouldCreateActivity: z.boolean(),
    /**
     * When this row was offered to a person, confirmed, or dismissed - the
     * offer-lifecycle stamps, mirroring email's. Optional on the wire: an older
     * deployed frontend must keep parsing a response that omits them.
     * `confirmedAt` on the CHOSEN match distinguishes a person-confirmed
     * allocation from one made before these stamps existed (both null); on a `proposal`
     * row it is always null (a confirmed row is never the live proposal).
     * `dismissedAt` is always null on `chosenMatch` (a dismissed row is never
     * chosen) and marks a dismissed offer on `proposal`.
     */
    confirmedAt: z.iso.datetime().nullable().optional(),
    dismissedAt: z.iso.datetime().nullable().optional(),
});
/**
 * A stored calendar event with decrypted content fields.
 * Mirrors `StoredCalendarEvent` in the Prisma schema; the encrypted blobs
 * (subject, description, location, attendeesJson, organiserJson) are decrypted
 * at the service layer before serialisation.
 */
export const StoredCalendarEventSchema = z.object({
    id: z.string(),
    calendarConnectionId: z.string(),
    providerEventId: z.string(),
    iCalUID: z.string(),
    recurringEventId: z.string().nullable(),
    originalStartTime: z.string().nullable(),
    startAt: z.string(),
    endAt: z.string(),
    isAllDay: z.boolean(),
    visibility: z.enum(['default', 'public', 'private', 'confidential']),
    showAs: z.enum(['free', 'tentative', 'busy', 'outOfOffice', 'workingElsewhere', 'unknown']),
    ownerResponseStatus: z.enum(['accepted', 'tentative', 'declined', 'needsAction']),
    subject: z.string(),
    description: z.string().nullable(),
    location: z.string().nullable(),
    attendees: z.array(CalendarEventAttendeeSchema),
    organiser: CalendarEventOrganiserSchema.nullable(),
    cancelledAt: z.string().nullable(),
    /**
     * The current client allocation for this event, or null when unmatched.
     * An event is allocated to at most one (client, project) tuple at a time.
     * null is an explicit state meaning "no match found" - not "unknown".
     */
    chosenMatch: CalendarClientMatchSchema.nullable(),
    /**
     * The event's live proposal, or the most recently dismissed one when there
     * is no live proposal - so a dismissed row still renders as "dismissed"
     * rather than "never offered" (mirrors email's `proposal` field). Null when
     * nothing has ever been offered. An allocated event can still carry a
     * dismissed proposal (a person who corrected or cleared the offer), and no
     * live one, so a reader must check `dismissedAt` before rendering it as an
     * offer. Optional on the wire for the same reason `chosenMatch` predates it.
     */
    proposal: CalendarClientMatchSchema.nullable().optional(),
});
export const StoredCalendarEventsResponseSchema = createPaginatedResponseSchema(StoredCalendarEventSchema);
/**
 * Query string for the stored-calendar-events list endpoint.
 *
 * `startFrom` and `startTo` are calendar dates (YYYY-MM-DD). The route layer
 * coerces them to UTC instants: `startFrom` -> 00:00:00.000Z of that day,
 * `startTo` -> 00:00:00.000Z of the day AFTER (so the WHERE is `[from, to+1d)`,
 * inclusive of `startTo`). Both are optional; together they form an inclusive
 * date range filter on `startAt`.
 */
export const StoredCalendarEventsQuerySchema = PaginationQuerySchema.extend({
    startFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional(),
    startTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional(),
});
/**
 * Query string for the account-wide stored-calendar-events list endpoint
 * (`GET /accounts/:accountId/stored-calendar-events`). Mirrors the per-connection
 * schema's `startFrom` / `startTo` semantics and adds the same client/project/
 * matched filters supported by the stored-emails list endpoint, so the two
 * surfaces stay at parity.
 *
 * `matchState='unmatched'` semantically contradicts `clientId` / `clientProjectId`
 * (no match means there is no client to filter on); the service drops those
 * filters when matchState is 'unmatched' rather than returning an empty list
 * silently or throwing.
 */
export const AccountStoredCalendarEventsQuerySchema = PaginationQuerySchema.extend({
    // Batch-resolve a known id set in one request (repeated `ids` params, as on
    // the clients list). Used by the activity diary to fetch source-event
    // previews for a page of activities without a per-id request fan-out.
    // Rows the caller cannot see (another member's connection) are silently
    // omitted, mirroring the endpoint's visibility scoping - absence, not an error.
    ids: z.array(z.uuid()).max(100).optional(),
    startFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional(),
    startTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional(),
    clientId: z.uuid().optional(),
    clientProjectId: z.uuid().optional(),
    matchState: z.enum(['matched', 'unmatched']).optional(),
});
// =============================================================================
// Calendar Event - Manual Match Management
// =============================================================================
export const CreateManualCalendarMatchInputSchema = z.object({
    clientId: z.uuid(),
    clientProjectId: z.uuid().optional(),
});
/**
 * Response shape for creating a manual calendar match.
 * The activityId is populated asynchronously by the reconcile listener;
 * callers should refetch the events list to get the settled state.
 */
export const ManualCalendarMatchResponseSchema = CalendarClientMatchSchema;
export const DeleteManualCalendarMatchParamsSchema = z.object({
    accountId: z.uuid(),
    storedCalendarEventId: z.uuid(),
    matchId: z.uuid(),
});
export const DeleteManualCalendarMatchResponseSchema = z.object({
    success: z.boolean(),
});
export const PromoteCalendarMatchParamsSchema = z.object({
    accountId: z.uuid(),
    storedCalendarEventId: z.uuid(),
    matchId: z.uuid(),
});
export const PromoteCalendarMatchResponseSchema = z.object({
    success: z.boolean(),
});
// =============================================================================
// Calendar Connection - E2E Bypass
// =============================================================================
export const E2ESeedCalendarConnectionInputSchema = z.object({
    accountId: z.uuid(),
    connectedByUserId: z.uuid(),
    provider: OAuthProviderSchema,
    emailAddress: z.email(),
});
export const E2ESeedCalendarConnectionResponseSchema = z.object({
    calendarConnectionId: z.string(),
});
export const E2EEnqueueCalendarReconciliationResponseSchema = z.object({
    enqueued: z.int().nonnegative(),
    enqueuedConnectionIds: z.array(z.uuid()),
});
export const E2EReconcileCalendarConnectionParamsSchema = z.object({
    connectionId: z.uuid(),
});
export const E2EReconcileCalendarConnectionResponseSchema = z.object({
    lastReconciliationAt: z.string().nullable(),
    lastReconciliationStatus: z.string().nullable(),
    consecutiveReconciliationFailures: z.int().nonnegative(),
});
export const E2EArchiveCalendarConnectionsInputSchema = z.object({
    ids: z.array(z.uuid()),
});
export const E2EArchiveCalendarConnectionsResponseSchema = z.object({
    archived: z.int().nonnegative(),
});
